import axios, { AxiosError } from "axios";
import { Token } from "../types/queries";
import { Errors } from "../types/api";
import { logAxiosError } from "../logging/logSentryError";
import { API_URL } from "./index";
import { UserSignInResponse, EmailPreferences, UpdateUserBodyParams, UserResponse, PublicUserResponse, MainEndpoints } from "@authory-packages/core";
import { getHeaders } from "./get-headers";
import { CommonArgs } from "./collections";
import { APPLICATION_ROUTES } from "../shared-logic/routes";
import { NotificationInlineLink } from "../components/ReusableStyledComponents/NotificationInlineLink";
import { NotificationHandler } from "./error-handler";
import { ContactUserFormik } from "../components/V3ContactUser";
import { ProfileUserResponse } from "@authory-packages/core";

const formatUserEndpoint = (user?: string) => {
    if (user) {
        return MainEndpoints.User.replace(":userId", user);
    } else {
        return MainEndpoints.User.split("/:userId")[0];
    }
};

export const getAuthenticatedUser = async (token: Token) => {
    if (token) {
        const { data } = await axios.get<UserResponse>(`${API_URL}${formatUserEndpoint()}`, getHeaders(token));

        return data;
    } else {
        return null;
    }
};

export const getPublicUser = async (slug: string) => {
    const { data } = await axios.get<PublicUserResponse>(`${API_URL}${MainEndpoints.UserPublic.replace(":userId", slug)}`);

    return data;
};

export const getUserProfile = async (slug: string, profileAccessKey: string | null) => {
    const { data } = await axios.get<ProfileUserResponse>(
        `${API_URL}${MainEndpoints.UserProfile.replace(":userId", slug).replace(":profileAccessKey?", profileAccessKey || "")}`
    );

    return data;
};

type UploadUserAvatarType = {
    token: string;
    file: File;
    userSlug: string;
};

export const uploadUserAvatar = async ({ token, file, userSlug }: UploadUserAvatarType) => {
    let formData = new FormData();
    formData.append("file", file);

    const res = await axios.post<UserResponse>(`${API_URL}${MainEndpoints.UpdateUserAvatar.replace(":userId?", userSlug)}`, formData, {
        headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "multipart/form-data"
        }
    });

    return res.data;
};

type DeleteUserAvatarArgs = {
    token: string;
    userSlug: string;
};

export const deleteUserAvatar = async ({ token, userSlug }: DeleteUserAvatarArgs) => {
    const res = await axios.post<UserResponse>(
        `${API_URL}${MainEndpoints.DeleteUserAvatar.replace(":userId?", userSlug)}`,
        {},
        {
            headers: {
                Authorization: "Bearer " + token
            }
        }
    );

    return res.data;
};

type updateUserArgs = {
    token: string;
    userSlug: string;
    data: UpdateUserBodyParams;
    skipOptimisticUpdate?: boolean;
    target?: string;
    skipAllUpdates?: boolean;
};

export const updateUser = async ({ token, userSlug, data }: updateUserArgs) => {
    const res = await axios.post<UserResponse>(
        `${API_URL}${MainEndpoints.UpdateUser.replace(":userId?", userSlug)}`,
        {
            ...data
        },
        {
            headers: {
                Authorization: "Bearer " + token
            }
        }
    );

    return res.data;
};

type ContactUserArgs = {
    userSlug: string;
    data: ContactUserFormik;
};

export const ContactUser = async ({ userSlug, data }: ContactUserArgs) => {
    const res = await axios.post<UserResponse>(`${API_URL}${MainEndpoints.UserContact.replace(":userId", userSlug)}`, {
        ...data
    });

    return res.data;
};

export const getEmailPreferences = async (token: string, userSlug: string) => {
    const { data } = await axios.get<EmailPreferences>(
        `${API_URL}${MainEndpoints.UserEmailpreferences.replace(":userId?", userSlug)}`,
        getHeaders(token)
    );

    return data;
};

interface saveEmailPreferencesArgs extends CommonArgs {
    data: EmailPreferences;
}

export const saveEmailPreferences = async ({ token, userSlug, data }: saveEmailPreferencesArgs) => {
    const res = await axios.post<EmailPreferences>(
        `${API_URL}${MainEndpoints.UpdateUserEmailpreferences.replace(":userId?", userSlug)}`,
        {
            ...data
        },
        getHeaders(token)
    );

    return res.data;
};

export const getNextIssuePreview = async (userSlug: string) => {
    const { data } = await axios.get(`${API_URL}${MainEndpoints.UserNextIssuePreviewStatic.replace(":userId?", userSlug)}`);

    return data;
};

type GDPRSettings = {
    privacyPolicy: string;
    imprint: string;
};

export const getGDPRSettings = async (token: string | null, userSlug: string) => {
    const { data } = await axios.get<GDPRSettings>(
        `${API_URL}${MainEndpoints.UserPrivacyPolicy.replace(":userId?", userSlug)}`,
        token ? getHeaders(token) : undefined
    );

    return data;
};

export const requestResetPassword = async ({ email }: { email: string }) => {
    const { data } = await axios.post(`${API_URL}${MainEndpoints.RequestResetPassword}`, { email });

    return data;
};

export const V3resetPasswordErrorHandler = (toastHandler: NotificationHandler) => async (error: AxiosError) => {
    if (error?.response?.status === Errors.State) {
        toastHandler(error?.response?.data?.message);
    }
    if (error?.response?.status === Errors.AccessDenied) {
        toastHandler(
            <>
                <span>
                    This password reset link seems to be no longer valid. You can request a new one{" "}
                    <NotificationInlineLink href={APPLICATION_ROUTES.LOGIN_FORGOTTEN_PASSWORD}>here</NotificationInlineLink>.
                </span>
            </>
        );
    } else {
        toastHandler("An unexpected error occured");
        logAxiosError(error);
    }
};

type ResetPasswordPayload = {
    userSlug: string;
    password: string;
    token: string;
};

export const resetPassword = async ({ userSlug, password, token }: ResetPasswordPayload) => {
    const { data } = await axios.post<UserSignInResponse>(`${API_URL}${MainEndpoints.UserResetPassword.replace(":userId", userSlug)}`, {
        password,
        token
    });

    return data;
};

export const clearLegacyProfileOgImage = async ({ token, userSlug }: CommonArgs) => {
    const { data } = await axios.post(`${API_URL}${MainEndpoints.DeleteUserOgImage.replace(":userId?", userSlug)}`, undefined, getHeaders(token));

    return data;
};

export const getPublicSmartShareProfile = async (userSlug: string, smartShareURL: string = "") => {
    const { data } = await axios.get<ProfileUserResponse>(
        `${API_URL}${MainEndpoints.UserSmartShareProfile.replace(":userId", userSlug).replace(":smartShareURL", smartShareURL)}`,
        undefined
    );

    return data;
};
